// Real-time Stats Bar - Shows live balance, P&L, and bot activity
import React, { useEffect, useState } from 'react';
import { useRealtime } from '../hooks/useRealtime';
import { Wifi, WifiOff, TrendingUp, TrendingDown, Activity, Zap, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const RealtimeStatsBar = () => {
    const { connected, balance, pnl, recentTrades, prices, requestSync } = useRealtime();
    const [flashBalance, setFlashBalance] = useState(false);
    const [prevBalance, setPrevBalance] = useState(balance.usdt);

    // Flash effect when balance changes
    useEffect(() => {
        if (balance.usdt !== prevBalance) {
            setFlashBalance(true);
            setTimeout(() => setFlashBalance(false), 1000);
            setPrevBalance(balance.usdt);
        }
    }, [balance.usdt, prevBalance]);

    // Format currency - more compact for mobile
    const formatCurrency = (value, compact = false) => {
        if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `$${(value / 1000).toFixed(compact ? 1 : 2)}K`;
        return `$${value.toFixed(compact ? 0 : 2)}`;
    };

    // Get latest trade
    const latestTrade = recentTrades[0];

    return (
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-[rgba(91,164,164,0.2)]">
            <div className="max-w-7xl mx-auto px-2 md:px-4 py-1.5 md:py-2">
                <div className="flex items-center justify-between gap-2 md:gap-4 text-[10px] md:text-sm">
                    {/* Connection Status */}
                    <div className="flex items-center gap-1 flex-shrink-0" data-testid="realtime-status">
                        {connected ? (
                            <span className="flex items-center gap-1 text-green-400" data-testid="realtime-connected">
                                <Wifi size={12} className="md:w-[14px] md:h-[14px] animate-pulse" />
                                <span className="hidden sm:inline text-[10px] md:text-sm">LIVE</span>
                            </span>
                        ) : (
                            <span className="flex items-center gap-1 text-red-400" data-testid="realtime-disconnected">
                                <WifiOff size={12} className="md:w-[14px] md:h-[14px]" />
                                <span className="hidden sm:inline text-[10px] md:text-sm">Offline</span>
                            </span>
                        )}
                    </div>

                    {/* Balance - Compact on mobile */}
                    <motion.div 
                        className={`flex items-center gap-1 md:gap-2 px-2 md:px-3 py-0.5 md:py-1 rounded-full ${
                            flashBalance ? 'bg-green-500/20' : 'bg-black/30'
                        } transition-colors min-w-0`}
                        animate={flashBalance ? { scale: [1, 1.05, 1] } : {}}
                        data-testid="realtime-balance"
                    >
                        <span className="text-gray-400 text-[9px] md:text-sm hidden sm:inline">Bal:</span>
                        <span className={`font-mono font-bold text-[10px] md:text-sm ${flashBalance ? 'text-green-400' : 'text-white'}`} data-testid="realtime-balance-value">
                            {formatCurrency(balance.usdt, true)}
                        </span>
                    </motion.div>

                    {/* P&L Stats - More compact */}
                    <div className="flex items-center gap-2 md:gap-4 min-w-0" data-testid="realtime-pnl-stats">
                        <div className="flex items-center gap-1">
                            <span className="text-gray-500 hidden md:inline">Total P&L:</span>
                            <span className="text-gray-500 md:hidden text-[9px]">P&L</span>
                            <span className={`font-mono text-[10px] md:text-sm ${pnl.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="realtime-total-pnl">
                                {pnl.total_pnl >= 0 ? '+' : ''}{formatCurrency(pnl.total_pnl, true)}
                            </span>
                        </div>
                        <div className="hidden sm:flex items-center gap-1">
                            <span className="text-gray-500 text-[10px] md:text-sm">Today:</span>
                            <span className={`font-mono text-[10px] md:text-sm ${pnl.today_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="realtime-today-pnl">
                                {pnl.today_pnl >= 0 ? '+' : ''}{formatCurrency(pnl.today_pnl, true)}
                            </span>
                        </div>
                        <div className="hidden md:flex items-center gap-1">
                            <span className="text-gray-500">Win Rate:</span>
                            <span className="font-mono text-[#5BA4A4]" data-testid="realtime-win-rate">{pnl.win_rate}%</span>
                        </div>
                    </div>

                    {/* Latest Trade */}
                    <AnimatePresence mode="wait">
                        {latestTrade && (
                            <motion.div 
                                key={latestTrade.id}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="hidden lg:flex items-center gap-2 px-3 py-1 bg-black/30 rounded-full"
                            >
                                <Zap size={12} className="text-[#5BA4A4]" />
                                <span className="text-gray-400">{latestTrade.symbol}</span>
                                <span className={`font-mono ${latestTrade.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {latestTrade.profit_loss >= 0 ? '+' : ''}${latestTrade.profit_loss?.toFixed(2)}
                                </span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Sync Button */}
                    <button 
                        onClick={requestSync}
                        className="p-1 md:p-1.5 hover:bg-white/10 rounded-full transition-colors flex-shrink-0"
                        title="Sync data"
                        data-testid="realtime-sync-btn"
                    >
                        <RefreshCw size={12} className="md:w-[14px] md:h-[14px] text-gray-400 hover:text-white" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RealtimeStatsBar;
